import { Link } from "react-router-dom";
import { Phone, Mail, Facebook, MapPin } from "lucide-react";
import { useSettings } from "@/lib/SettingsContext";

export const Footer = () => {
  const { brand: BRAND, locations: LOCATIONS, images: IMAGES } = useSettings();
  return (
    <footer data-testid="site-footer" className="bg-plum text-white mt-24">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16 grid md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          {IMAGES?.logo ? (
            <img
              src={IMAGES.logo}
              alt={BRAND.name}
              data-testid="footer-logo-image"
              className="h-24 w-auto object-contain brightness-0 invert opacity-90"
            />
          ) : (
            <>
              <div className="font-display italic text-4xl">{BRAND.name}</div>
              <div className="text-xs tracking-[0.3em] uppercase text-blush/80 mt-1">
                {BRAND.tagline}
              </div>
            </>
          )}
          <p className="mt-6 text-blush/85 max-w-md leading-relaxed">
            A serene escape in {BRAND.area}. Tailored massage, facials & nail care
            with Deborah Louise — using nourishing Dr Hauschka products.
          </p>
          <div className="flex gap-3 mt-6">
            <a
              href={BRAND.facebook}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-facebook-link"
              className="rounded-full border border-white/30 p-2.5 hover:bg-white/10 transition"
              aria-label="Facebook"
            >
              <Facebook size={16} />
            </a>
            <a
              href={`tel:${BRAND.phoneTel}`}
              data-testid="footer-phone-link"
              className="rounded-full border border-white/30 p-2.5 hover:bg-white/10 transition"
              aria-label="Phone"
            >
              <Phone size={16} />
            </a>
            <a
              href={`mailto:${BRAND.email}`}
              data-testid="footer-email-link"
              className="rounded-full border border-white/30 p-2.5 hover:bg-white/10 transition"
              aria-label="Email"
            >
              <Mail size={16} />
            </a>
          </div>
        </div>

        <div>
          <h4 className="text-sm uppercase tracking-[0.25em] text-blush/70 mb-4">Visit</h4>
          <ul className="space-y-4 text-sm text-blush/90">
            {LOCATIONS.map((l) => (
              <li key={l.id}>
                <div className="flex items-start gap-2">
                  <MapPin size={14} className="mt-0.5 text-champagne shrink-0" />
                  <div>
                    <div className="font-semibold text-white">{l.name}</div>
                    <div className="text-blush/70 text-xs">{l.address}</div>
                    <div className="text-blush/90 mt-0.5">{l.hours}</div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="text-sm uppercase tracking-[0.25em] text-blush/70 mb-4">Get in touch</h4>
          <ul className="space-y-3 text-sm">
            <li>
              <a
                href={`tel:${BRAND.phoneTel}`}
                className="hover:text-champagne transition"
                data-testid="footer-phone-text"
              >
                {BRAND.phone}
              </a>
            </li>
            <li>
              <a
                href={`mailto:${BRAND.email}`}
                className="hover:text-champagne transition break-all"
                data-testid="footer-email-text"
              >
                {BRAND.email}
              </a>
            </li>
            <li className="pt-3">
              <Link
                to="/contact"
                data-testid="footer-book-cta"
                className="inline-flex items-center rounded-full bg-white text-plum px-5 py-2 text-sm font-medium hover:bg-blush transition"
              >
                Book a treatment
              </Link>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/15">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-6 flex flex-col md:flex-row justify-between gap-2 text-xs text-blush/70">
          <span>© {new Date().getFullYear()} Beautify · Deborah Louise Beauty Cornwall</span>
          <span>Made with care in Wadebridge, Cornwall.</span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
